/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { registry } from '@/lib/page-designer/registry';

// STATIC_REGISTRY_START

/**
 * Initialize the static component registry.
 * This function is auto-generated by the staticRegistry Vite plugin.
 *
 * DO NOT EDIT THIS FUNCTION MANUALLY - it will be overwritten on next build.
 *
 * Components registered: Content.announcementBanner, Content.contentCard, Content.hero, Content.pdImage, Content.popularCategory, Content.productRecommendations, Content.productTile, Layout.burgerMenuEmbeddedRegions, Layout.grid, Layout.header, Layout.heroCarousel, Layout.megaMenu, Layout.popularCategories, Layout.productCarousel, storefrontnext_base.burgerMenuFooter, storefrontnext_base.categorySubmenuSlot, storefrontnext_base.contentCard, storefrontnext_base.exampleBanner, storefrontnext_base.footerLink, storefrontnext_base.footerLinkList, storefrontnext_base.footerPaymentMethodIcon, storefrontnext_base.footerPaymentMethodIconList, storefrontnext_base.footerSocialMediaIcon, storefrontnext_base.footerSocialMediaIconList, storefrontnext_base.image, storefrontnext_base.insideSpace, storefrontnext_base.keyline, storefrontnext_base.linkCard, storefrontnext_base.linkColumn, storefrontnext_base.menuItem, storefrontnext_base.menuItemList, storefrontnext_base.nestedRegion, storefrontnext_base.trendingCarousel, storefrontnext_base.trendingItem
 */
export function initializeRegistry(targetRegistry = registry): void {
    targetRegistry.registerImporter(
        'Content.announcementBanner',
        () => import('../../components/announcement-banner/index'),
        { fallback: 'fallback' }
    );
    targetRegistry.registerImporter('Content.contentCard', () => import('../../components/content-card/index'));
    targetRegistry.registerImporter('Content.hero', () => import('../../components/hero/index'));
    targetRegistry.registerImporter('Content.pdImage', () => import('../../components/dynamic-image/index'));
    targetRegistry.registerImporter(
        'Content.popularCategory',
        () => import('../../components/home/popular-category/index'),
        { loader: 'loader' }
    );
    targetRegistry.registerImporter(
        'Content.productRecommendations',
        () => import('../../components/product-recommendations/index'),
        { loader: 'loader' }
    );
    targetRegistry.registerImporter('Content.productTile', () => import('../../components/product-tile/index'), {
        loader: 'loader',
    });
    targetRegistry.registerImporter(
        'Layout.burgerMenuEmbeddedRegions',
        () => import('../../components/page-designer/burger-menu-embedded-regions/index')
    );
    targetRegistry.registerImporter('Layout.grid', () => import('../../components/grid/index'));
    targetRegistry.registerImporter('Layout.header', () => import('../../components/header/index'));
    targetRegistry.registerImporter('Layout.heroCarousel', () => import('../../components/hero-carousel/index'), {
        fallback: 'fallback',
    });
    targetRegistry.registerImporter('Layout.megaMenu', () => import('../../components/navigation-menu-mega/index'));
    targetRegistry.registerImporter(
        'Layout.popularCategories',
        () => import('../../components/home/popular-categories/index'),
        { loader: 'loader' }
    );
    targetRegistry.registerImporter('Layout.productCarousel', () => import('../../components/product-carousel/index'), {
        loader: 'loader',
        fallback: 'fallback',
    });
    targetRegistry.registerImporter(
        'storefrontnext_base.burgerMenuFooter',
        () => import('../../components/page-designer/burger-menu-footer/index')
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.categorySubmenuSlot',
        () => import('../../components/page-designer/category-submenu-slot/index')
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.contentCard',
        () => import('../../components/page-designer/content-card/index'),
        { fallback: 'fallback' }
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.exampleBanner',
        () => import('../../components/page-designer/example-banner/index')
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.footerLink',
        () => import('../../components/page-designer/footer-link/index')
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.footerLinkList',
        () => import('../../components/page-designer/footer-link-list/index')
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.footerPaymentMethodIcon',
        () => import('../../components/page-designer/footer-payment-method-icon/index')
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.footerPaymentMethodIconList',
        () => import('../../components/page-designer/footer-payment-method-icon-list/index')
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.footerSocialMediaIcon',
        () => import('../../components/page-designer/footer-social-media-icon/index')
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.footerSocialMediaIconList',
        () => import('../../components/page-designer/footer-social-media-icon-list/index')
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.image',
        () => import('../../components/page-designer/image/index'),
        { fallback: 'fallback' }
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.insideSpace',
        () => import('../../components/page-designer/inside-space/index'),
        { fallback: 'fallback' }
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.keyline',
        () => import('../../components/page-designer/keyline/index'),
        { fallback: 'fallback' }
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.linkCard',
        () => import('../../components/page-designer/link-card/index'),
        { fallback: 'fallback' }
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.linkColumn',
        () => import('../../components/page-designer/link-column/index'),
        { fallback: 'fallback' }
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.menuItem',
        () => import('../../components/page-designer/menu-item/index')
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.menuItemList',
        () => import('../../components/page-designer/menu-item-list/index')
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.nestedRegion',
        () => import('../../components/page-designer/nested-region/index')
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.trendingCarousel',
        () => import('../../components/page-designer/trending-carousel/index'),
        { fallback: 'fallback' }
    );
    targetRegistry.registerImporter(
        'storefrontnext_base.trendingItem',
        () => import('../../components/page-designer/trending-item/index'),
        { fallback: 'fallback' }
    );
}

// STATIC_REGISTRY_END
